import React, { useState } from 'react';
import { Form } from 'redux-form';
import PropTypes from 'prop-types';
import { map, range, without } from 'lodash';
import Select from '../../../common/select';
import LockableInput from '../../../common/LockableInput';
import LockableSelect from '../../../common/LockableSelect';
import Textarea from '../../../common/textarea';
import CurrencyInput from '../../../common/currencyInput';
import CheckboxSwitch from '../../../common/checkboxSwitch';
import SelectMultiple from '../../../common/SelectMultiple';
import Tooltip from '../../../common/tooltip';
import { minuteOptionShape } from '../../../common/hoc/withMinuteOptions';
import LocalizationTabs from './LocalizationTabs';
import { Tag } from '../../shops/prepayment-settings/tag/tag.component';
import { TextM } from '../../../../modules/infra/dls/components/typography';
import { t } from '../../../../modules/infra/i18n';

import styles from './styles.module.scss';
import { useFeatureFlags } from '../../../../modules/infra/feature-flags';

export const ServiceDetailsForm = ({
  categories,
  durationHour,
  form,
  handleSubmit,
  isLimitedCategoryEditAccess,
  isLimitedEditAccess,
  minuteOptions,
  roleIdType,
  selectedLanguagesCodes,
  taxes,
  onAddLanguage,
  onRemoveLanguage,
  supportedLanguagesCodes,
  defaultLanguage,
}) => {
  const { paymentDepositsEnabled } = useFeatureFlags();
  const [currentLanguage, setCurrentLanguage] = useState(selectedLanguagesCodes.find(lang => lang === defaultLanguage));
  let minutes = minuteOptions;

  if (Number(durationHour) < 1) {
    minutes = without(minutes, 5);
  }

  const categorySelectOptions = map(categories, category => ({
    label: category.name,
    value: category.id,
  }));

  const taxSelectOptions = map(taxes, tax => ({
    label: `${tax.name} ${tax.percentage}%`,
    value: tax.id,
  }));

  const LIMITED_PERMISSIONS_MESSAGE = t('services:form.details.limited_permissions_message');

  return (
    <Form
      id={form}
      onSubmit={handleSubmit}
      className="u-max-width-710 c-wrapper-allow-header"
    >
      <div className="c-inner-wrapper u-pt u-br-12">
        <div className="c-inner-wrapper u-max-width-710">
          <LocalizationTabs
            selectedLanguagesCodes={selectedLanguagesCodes}
            supportedLanguagesCodes={supportedLanguagesCodes}
            currentLanguageCode={currentLanguage}
            defaultLanguageCode={defaultLanguage}
            onAddLanguage={onAddLanguage}
            onRemoveLanguage={onRemoveLanguage}
            onChangeCurrentLanguage={setCurrentLanguage}
          >
            <div className="c-label-new">{t('services:form.details.service_name')}</div>
            <div className="o-flexbox u-pb-">
              <div className="o-flexbox__item u-flex-1">
                <LockableInput
                  disabled={isLimitedEditAccess}
                  hint={LIMITED_PERMISSIONS_MESSAGE}
                  className="c-input"
                  name={`localizedName.${currentLanguage}`}
                  type="text"
                  placeholder={t('services:form.details.service_name_placeholder')}
                />
              </div>
            </div>
            <div className="c-label-new u-mt--">{t('services:form.details.description')}</div>
            <div className="o-flexbox u-pb-">
              <div className="o-flexbox__item u-flex-1 c-protected-input">
                <Textarea
                  disabled={isLimitedEditAccess}
                  className="c-input u-min-height-66"
                  placeholder={t('services:form.details.description_placeholder')}
                  name={`localizedDesc.${currentLanguage}`}
                  maxLength="300"
                />
                <div display-if={isLimitedEditAccess} className="c-protected-input-trigger">
                  <span className="c-protected-input-popup">
                    {LIMITED_PERMISSIONS_MESSAGE}
                  </span>
                </div>
              </div>
            </div>
          </LocalizationTabs>
          <div className="c-label-new u-mt-">{t('services:form.details.category')}</div>
          <LockableSelect
            disabled={isLimitedCategoryEditAccess}
            hint={LIMITED_PERMISSIONS_MESSAGE}
            className="c-select"
            name="serviceCategoriesId"
            parse={val => val || null}
            options={categorySelectOptions}
            empty
          />
          {!!taxSelectOptions.length && (
            <div className="o-flexbox o-flexbox--justify-start o-flexbox--content-align-center u-mt-">
              <div className="c-label-new u-mv--">
                {t('services:form.details.tax_label')}
              </div>
              <Tooltip className="c-info-tooltip-new--form">
                <div className="u-m--">
                  {t('services:form.details.tax_tooltip')}
                </div>
              </Tooltip>
            </div>
          )}
          <SelectMultiple
            name="tax"
            options={taxSelectOptions}
            direction="row"
          />
          {roleIdType.type === 'brand' && (
            <div className="o-flexbox o-flexbox--justify-start o-flexbox--content-align-center u-mt-">
              <div className="c-heading--small c-heading--semibold">
                {t('services:form.details.defaults_heading')}
              </div>
              <Tooltip className="c-info-tooltip-new--form">
                <div className="u-m--">
                  {t('services:form.details.defaults_tooltip')}
                </div>
              </Tooltip>
            </div>
          )}
          <div className="layout layout--small u-mt-">
            <div className="layout__item u-2/3-large-and-up">
              <div className="c-label-new u-mt--">{t('services:form.details.service_duration')}</div>
              <div className="layout layout--flush">
                <div className="layout__item u-1/2">
                  <Select className="c-select" name="durationHour" options={range(0, 13)} suffix={t('services:form.details.duration.hours')} />
                </div>
                <div className="layout__item u-1/2">
                  <Select className="c-select" name="durationMinute" options={minutes} suffix={t('services:form.details.duration.min')} />
                </div>
              </div>
            </div>
            <div className="layout__item u-1/3-large-and-up">
              <div className="c-label-new u-mt--">{t('services:form.details.pricing')}</div>
              <CurrencyInput className="c-input" type="number" placeholder={t('services:form.details.pricing_placeholder')} name="cost" />
            </div>
          </div>
          {!paymentDepositsEnabled && (
            <>
              <div className="o-flexbox o-flexbox--justify-start o-flexbox--content-align-center u-pv- u-mt">
                <CheckboxSwitch
                  name="enabled"
                  id="enabled"
                  label={t('services:form.details.online_visibility')}
                  wrapperClass="u--mr--"
                />
                <Tooltip className="c-info-tooltip-new--form u-mt--">
                  <div className="u-m--">
                    {t('services:form.details.online_visibility_tooltip')}
                  </div>
                </Tooltip>
              </div>
              <div className="o-flexbox o-flexbox--justify-start o-flexbox--content-align-center">
                <CheckboxSwitch
                  name="requiresPrepaid"
                  id="requiresPrepaid"
                  label={t('services:form.details.requires_prepaid')}
                  wrapperClass="u--mr--"
                />
                <Tooltip className="c-info-tooltip-new--form u-mt--">
                  <div className="u-m--">
                    {t('services:form.details.requires_prepaid_tooltip')}
                  </div>
                </Tooltip>
              </div>
            </>
          )}
          {paymentDepositsEnabled && (
            <div>
              <div className="c-heading--large c-heading--dark large-and-up-mv c-heading--main-header">
                {t('services:form.details.visibility_payment_settings')}
              </div>
              <div className="o-flexbox o-flexbox--justify-start o-flexbox--content-start o-flexbox--column u-pv- u-mt">
                <CheckboxSwitch
                  name="enabled"
                  id="enabled"
                  wrapperClass={styles.checkboxSwitch}
                  label={
                    <>
                      <p className={styles.checkboxSwitchLabel}>{t('services:form.details.visible_online')}</p>
                      <p className={styles.checkboxSwitchLabelSecondary}>{t('services:form.details.visible_online_description')}</p>
                    </>
                  }
                />
              </div>
              <div className="c-separator u--mh u-mt- u-mb- medium-and-up-mv- large-and-up-u-mv" />
                <div className="o-flexbox o-flexbox--justify-start o-flexbox--content-start o-flexbox--column u-mt u-mb">
                <CheckboxSwitch
                  name="requiresPrepaid"
                  id="requiresPrepaid"
                  wrapperClass={styles.checkboxSwitch}
                  label={
                    <>
                      <p className={styles.checkboxSwitchLabel}>
                        {t('services:form.details.require_prepayment')} <Tag>{t('services:form.details.require_prepayment_tag')}</Tag>
                      </p>
                      <p className={styles.checkboxSwitchLabelSecondary}>
                        {t('services:form.details.require_prepayment_description')}
                      </p>
                    </>
                  }
                />
              </div>
            </div>
          )}
          <div className="c-separator u--mh u-mt- u-mb- medium-and-up-mv- large-and-up-u-mv" />
          {roleIdType.type !== 'barber' && (
            <div
              className="o-flexbox o-flexbox--justify-start o-flexbox--content-align-center c-protected-input u-pv-"
            >
              <CheckboxSwitch
                name="kioskEnabled"
                id="kioskEnabled"
                label={t('services:form.details.kiosk')}
                wrapperClass="u--mr--"
              />
              <Tooltip className="c-info-tooltip-new--form u-mt--">
                <div className="u-m--">
                  {t('services:form.details.kiosk_tooltip')}
                </div>
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </Form>
  );
};

ServiceDetailsForm.propTypes = {
  isLimitedEditAccess: PropTypes.bool.isRequired,
  isLimitedCategoryEditAccess: PropTypes.bool.isRequired,
  roleIdType: PropTypes.object.isRequired,
  durationHour: PropTypes.number,
  minuteOptions: minuteOptionShape,
  categories: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  taxes: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string, name: PropTypes.string })),
  // eslint-disable-next-line react/no-unused-prop-types
  initialValues: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  form: PropTypes.string.isRequired,
  onAddLanguage: PropTypes.func.isRequired,
  onRemoveLanguage: PropTypes.func.isRequired,
  supportedLanguagesCodes: PropTypes.arrayOf((PropTypes.string)).isRequired,
  defaultLanguage: PropTypes.string.isRequired,
  selectedLanguagesCodes: PropTypes.arrayOf((PropTypes.string)).isRequired,
};

ServiceDetailsForm.defaultProps = {
  durationHour: 0,
  minuteOptions: [],
  categories: [],
  taxes: [],
  initialValues: {},
};

export default ServiceDetailsForm;
